import {
	brandContentConfigData,
	AuthContent,
	defaultConfigData,
	gbpeCelestiqModalActivated,
} from '../../../src/ts/config/auth-content-config'
import { GlobalNav } from '../components/global-nav/global-nav'
import { gbpeContentLoadedCheck } from './portable-experiences'
import { getBrandFromUrl, isBrandSite, isNonCelestiqPage } from './quantum'
import { GBModalService } from '@gbds/quantum/src/ts/utils/modal-service'

export class AuthContentService {
	private _configEntries: AuthContent[]
	private _globalNav: GlobalNav

	constructor() {
		this._loadConfigFileContents()
		gbpeContentLoadedCheck(() => {
			this._initService()
		})
	}

	private _loadConfigFileContents() {
		const _brand = getBrandFromUrl()
		this._getDefaultConfiguration()
		this._getBrandConfiguration(_brand)
	}

	private async _initService() {
		this._globalNav = document.querySelector('gb-global-nav') as GlobalNav
		this._evaluateAttributeCallback()
	}

	private _getDefaultConfiguration() {
		this._configEntries = Object.values(defaultConfigData)
	}

	private _getBrandConfiguration(brand: string) {
		const brandConfigEntries: AuthContent[] = Object.values(brandContentConfigData).filter(configEntry => {
			if (configEntry.brand === brand) return configEntry
		})[0]?.config
		brandConfigEntries ? (this._configEntries = this._configEntries.concat(brandConfigEntries)) : ''
	}

	private _evaluateAttributeCallback() {
		this._configEntries?.forEach(config => {
			this[config.callback] ? this[config.callback](config) : ''
		})
	}

	private _validateAuthLinks() {
		this._isAttributeActive(this.getConfigFileRecord('auth'), this._isAuth())
	}

	private _validateUnauthLinks() {
		this._isAttributeActive(this.getConfigFileRecord('unauth'), !this._isAuth())
	}

	private async _validateRewardsLinks() {
		if (this._isAuth()) {
			this._isAttributeActive(this.getConfigFileRecord('rewards'), await this._isRewardsEnabled())
		}
	}

	private async _validateNonRewardsLinks() {
		if (this._isAuth()) {
			this._isAttributeActive(this.getConfigFileRecord('nonrewards'), !(await this._isRewardsEnabled()))
		}
	}

	private async _validateCelestiqLinks() {
		this._isAttributeActive(this.getConfigFileRecord('celestiq'), await this.isCelestiqUser())
		//Triggers open-modal event
		//Event listener in Quantum solar/modal.js
		if (
			this._canOpenModal() &&
			(await this.isCelestiqUser()) &&
			globalThis?.gmwp &&
			isBrandSite() &&
			isNonCelestiqPage()
		) {
			this.triggerCelestiqModal()
			this._setLocalStorage()
		}
	}

	public async triggerCelestiqModal() {
		const celestiqModalContent = document
			?.querySelector('gb-global-nav')
			?.shadowRoot.querySelector('[data-celestiq-modal-partial]')
			?.getAttribute('data-celestiq-modal-partial')
		if ((await this.isCelestiqUser()) && celestiqModalContent) {
			const celestiqJsonDetail = JSON.parse(celestiqModalContent)

			try {
				GBModalService.openModal({
					modalId: 'celestiq-auth-modal',
					title: celestiqJsonDetail.title || '',
					contentPath: `${celestiqJsonDetail.url}.html`,
					isFullscreen: celestiqJsonDetail.fullScreenModal,
				})
			} catch (error) {
				console.log(error)
			}
		}
	}

	private _setLocalStorage() {
		localStorage.setItem(gbpeCelestiqModalActivated, 'true')
	}

	private _canOpenModal() {
		return localStorage.getItem(gbpeCelestiqModalActivated) === 'true' ? false : true
	}

	private _isAttributeActive(authContentValue: AuthContent, display = false) {
		this._setDisplay(authContentValue, display)
		display ? this._setAttributesInDOM(authContentValue.attributeName) : ''
	}

	private _setDisplay(authContentValue, display: boolean) {
		authContentValue.display = display ? 'show' : 'hide'
	}

	private _setAttributesInDOM(attibuteName) {
		if (isBrandSite()) {
			this._setBodyAttribute(attibuteName)
		}
		this._setGlobalNavAttribute(attibuteName)
	}

	private _setBodyAttribute(attibuteName: string) {
		this._setDataAttribute(document.body, attibuteName)
	}

	private _setGlobalNavAttribute(attibuteName: string) {
		this._setDataAttribute(this._globalNav, attibuteName)
	}

	private _setDataAttribute(container: HTMLElement | GlobalNav, attibuteName: string) {
		let attributeList = container?.getAttribute('data-auth-content-active')
		attributeList = attributeList ? `${attributeList};${attibuteName}` : attibuteName
		container?.setAttribute('data-auth-content-active', attributeList)
	}

	private _isAuth() {
		const isAuth = window.gbpe.auth.currentAuthState === 1 ? true : false
		return isAuth
	}

	private async _isRewardsEnabled() {
		const _rewards = await window.gbpe.user.rewards
		const isRewardsUser =
			_rewards?.data?.reward?.status && _rewards?.data?.reward?.status === 'Active' ? true : false
		return isRewardsUser
	}

	private async isCelestiqUser() {
		await window.gbpe.entitlementService.loaded
		return window.gbpe.entitlementService.isCelestiq
	}

	public get data(): AuthContent[] {
		return this._configEntries
	}

	public getConfigFileRecord(name: string): AuthContent {
		const configVal = this._configEntries?.filter(config => config?.name === name)[0]
		return configVal
	}
}
